import type { Map as MapboxMap, GeoJSONSource } from "mapbox-gl";
import type { MunicipalitiesResponse, GeoJsonFeature, MunicipalityFeatureProperties } from "../../api/map/types";
import { applyIncrementalUpdate, setCachedFeatures, getCachedFeatures } from "./utils";

const SOURCE_ID = "municipalities";
const HIGHLIGHT_SOURCE_ID = "municipality-highlight";
const HIGHLIGHT_LAYER_ID = "municipality-highlight-line";

/**
 * Ensures the municipalities layer exists on the map (idempotent).
 * Returns a promise that resolves when the layer is ready (same pattern as PostalCodesLayer for style.load).
 */
export const ensureMunicipalitiesLayer = (map: MapboxMap): Promise<void> => {
    return new Promise((resolve) => {
        const createLayers = () => {
            try {
                if (!map.getSource(SOURCE_ID)) {
                    map.addSource(SOURCE_ID, {
                        type: "geojson",
                        data: { type: "FeatureCollection", features: [] },
                        promoteId: "municipalityPolygonId"
                    });
                }

                if (!map.getLayer(`${SOURCE_ID}-fill`)) {
                    map.addLayer({
                        id: `${SOURCE_ID}-fill`,
                        type: "fill",
                        source: SOURCE_ID,
                        paint: {
                            "fill-color": [
                                "match",
                                ["get", "colorIndex"],
                                0, "#3b82f6",
                                1, "#ef4444",
                                2, "#22c55e",
                                3, "#f59e0b",
                                4, "#8b5cf6",
                                5, "#ec4899",
                                6, "#06b6d4",
                                7, "#f97316",
                                8, "#84cc16",
                                9, "#6366f1",
                                "#3b82f6"
                            ],
                            "fill-opacity": [
                                "case",
                                ["boolean", ["feature-state", "selected"], false],
                                0,
                                0.3
                            ]
                        }
                    });
                }

                if (!map.getLayer(`${SOURCE_ID}-outline`)) {
                    map.addLayer({
                        id: `${SOURCE_ID}-outline`,
                        type: "line",
                        source: SOURCE_ID,
                        paint: {
                            "line-color": [
                                "case",
                                ["boolean", ["feature-state", "selected"], false],
                                "#0f172a",
                                [
                                    "match",
                                    ["get", "colorIndex"],
                                    0, "#1e40af",
                                    1, "#dc2626",
                                    2, "#16a34a",
                                    3, "#d97706",
                                    4, "#7c3aed",
                                    5, "#db2777",
                                    6, "#0891b2",
                                    7, "#ea580c",
                                    8, "#65a30d",
                                    9, "#4f46e5",
                                    "#1e40af"
                                ]
                            ],
                            "line-width": [
                                "case",
                                ["boolean", ["feature-state", "selected"], false],
                                ["interpolate", ["linear"], ["zoom"], 10, 3, 14, 4, 18, 5],
                                ["interpolate", ["linear"], ["zoom"], 10, 1, 15, 1.5, 18, 2]
                            ],
                            "line-opacity": [
                                "case",
                                ["boolean", ["feature-state", "selected"], false],
                                1,
                                0.6
                            ]
                        }
                    });
                }

                if (!map.getLayer(`${SOURCE_ID}-labels`)) {
                    map.addLayer({
                        id: `${SOURCE_ID}-labels`,
                        type: "symbol",
                        source: SOURCE_ID,
                        layout: {
                            "text-field": ["get", "code"],
                            "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
                            "text-size": 11,
                            "text-anchor": "center",
                            "text-allow-overlap": false
                        },
                        paint: {
                            "text-color": "#1e40af",
                            "text-halo-color": "#ffffff",
                            "text-halo-width": 1.5
                        }
                    });
                }

                if (!map.getSource(HIGHLIGHT_SOURCE_ID)) {
                    map.addSource(HIGHLIGHT_SOURCE_ID, {
                        type: "geojson",
                        data: { type: "FeatureCollection", features: [] }
                    });
                }
                if (!map.getLayer(HIGHLIGHT_LAYER_ID)) {
                    map.addLayer({
                        id: HIGHLIGHT_LAYER_ID,
                        type: "line",
                        source: HIGHLIGHT_SOURCE_ID,
                        paint: {
                            "line-color": "#0f172a",
                            "line-width": ["interpolate", ["linear"], ["zoom"], 10, 3, 14, 4, 18, 5],
                            "line-opacity": 1
                        }
                    });
                }

                resolve();
            } catch (error) {
                if (!map.isStyleLoaded()) {
                    map.once("style.load", () => {
                        createLayers();
                    });
                } else {
                    throw error;
                }
            }
        };

        if (!map.isStyleLoaded()) {
            const styleLoadHandler = () => {
                if (map.isStyleLoaded()) {
                    createLayers();
                } else {
                    setTimeout(() => {
                        if (map.isStyleLoaded()) {
                            createLayers();
                        } else {
                            resolve();
                        }
                    }, 100);
                }
            };
            map.once("style.load", styleLoadHandler);

            const checkInterval = setInterval(() => {
                if (map.isStyleLoaded()) {
                    clearInterval(checkInterval);
                    map.off("style.load", styleLoadHandler);
                    createLayers();
                }
            }, 100);

            setTimeout(() => {
                clearInterval(checkInterval);
            }, 5000);
        } else {
            createLayers();
        }
    });
};

/**
 * Updates the municipalities layer with data from the API.
 */
export const updateMunicipalitiesLayer = (
    map: MapboxMap,
    data: MunicipalitiesResponse | null,
    useIncremental: boolean = true
): void => {
    const source = map.getSource(SOURCE_ID);
    if (!source || source.type !== "geojson") {
        return;
    }

    const geojsonSource = source as GeoJSONSource;

    if (!data || data.features.length === 0) {
        geojsonSource.setData({ type: "FeatureCollection", features: [] });
        return;
    }

    const newFeatures: GeoJsonFeature<MunicipalityFeatureProperties>[] = data.features.map((feature) => ({
        type: "Feature" as const,
        id: feature.id ?? feature.properties.municipalityPolygonId,
        geometry: feature.geometry,
        properties: {
            ...feature.properties
        }
    }));

    if (useIncremental) {
        try {
            applyIncrementalUpdate(
                map,
                geojsonSource,
                SOURCE_ID,
                newFeatures,
                (feature) => feature.id ?? feature.properties.municipalityPolygonId
            );
            return;
        } catch {
            // Fallback to full update
        }
    }

    geojsonSource.setData({
        type: "FeatureCollection" as const,
        features: newFeatures
    } as any);
    setCachedFeatures(map, SOURCE_ID, newFeatures);
};

/**
 * Highlights the selected municipality with a clear outline (no fill).
 * Uses a dedicated source/layer with the feature from the list so the ram is always visible.
 */
export const updateMunicipalityHighlight = (
    map: MapboxMap,
    selectedFeature: GeoJsonFeature<MunicipalityFeatureProperties> | null
): void => {
    const source = map.getSource(HIGHLIGHT_SOURCE_ID);
    if (!source || source.type !== "geojson") return;
    const geojson = selectedFeature
        ? { type: "FeatureCollection" as const, features: [selectedFeature] }
        : { type: "FeatureCollection" as const, features: [] };
    (source as GeoJSONSource).setData(geojson as any);
};

/**
 * Sets feature-state highlight on the main layer (fallback). Kept for compatibility.
 */
export const setMunicipalitySelectionHighlight = (
    map: MapboxMap,
    selectedMunicipalityId: string | null
): void => {
    const features = getCachedFeatures<MunicipalityFeatureProperties>(map, SOURCE_ID);
    if (!features.length) return;
    const selectedNorm = selectedMunicipalityId != null ? String(selectedMunicipalityId).trim() : null;
    for (const f of features) {
        const id = f.properties?.municipalityPolygonId ?? f.id;
        if (id == null) continue;
        const idStr = String(id);
        const isSelected = selectedNorm !== null && (idStr === selectedNorm || idStr === selectedMunicipalityId);
        try {
            map.setFeatureState({ source: SOURCE_ID, id: idStr }, { selected: isSelected });
        } catch {
            // ignore
        }
    }
};

/**
 * Sets the visibility of the municipalities layer.
 */
export const setMunicipalitiesLayerVisibility = (map: MapboxMap, visible: boolean): void => {
    const visibility = visible ? "visible" : "none";
    if (map.getLayer(`${SOURCE_ID}-fill`)) {
        map.setLayoutProperty(`${SOURCE_ID}-fill`, "visibility", visibility);
    }
    if (map.getLayer(`${SOURCE_ID}-outline`)) {
        map.setLayoutProperty(`${SOURCE_ID}-outline`, "visibility", visibility);
    }
    if (map.getLayer(`${SOURCE_ID}-labels`)) {
        map.setLayoutProperty(`${SOURCE_ID}-labels`, "visibility", visibility);
    }
};
